import { motion } from 'framer-motion'
import { RevealLines } from './Reveal'
import { useT } from '../i18n/locale.jsx'
import { BOOKING_URL } from '../config'
import { calAttrs } from '../cal'

// Hero în compoziția template-ului (media dark full-screen + statement jos-stânga,
// eyebrow mono lateral), cu identitatea vorba: sensul cuvântului + propunerea de valoare.
const EASE = [0.16, 1, 0.3, 1]

export default function Hero() {
  const t = useT().hero
  const headline = [
    t.line1,
    <>
      {t.line2a}
      <em className="display-italic text-honey">{t.em}</em>
      {t.line2b}
    </>,
  ]
  return (
    <section data-theme="dark" className="relative min-h-screen overflow-hidden bg-forest text-cream">
      {/* Media de fundal */}
      <motion.div
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: EASE }}
        className="absolute inset-0"
      >
        <img
          src="/hero.webp"
          alt=""
          width="1672"
          height="941"
          fetchpriority="high"
          decoding="async"
          className="h-full w-full object-cover opacity-90"
        />
        {/* Gradient ușor: imaginea e deja întunecată pe stânga, deci nu o sufoc.
            Întăresc doar baza + stânga pentru lizibilitatea titlului. */}
        <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest/75 via-transparent to-transparent" />
      </motion.div>

      {/* Accent honey */}
      <div className="absolute right-5 top-28 z-10 text-honey md:right-10">
        <span className="text-lg">✦</span>
      </div>

      {/* Bloc jos: definiție dicționar (sensul cuvântului) + statement + sub + CTA */}
      <div className="absolute inset-x-0 bottom-12 z-10 mx-auto max-w-site px-5 md:bottom-16 md:px-10">
        {/* Intrare de dicționar — vizibilă la orice lățime */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
          className="mb-7 font-mono text-[11px] uppercase leading-relaxed tracking-[0.1em] md:text-[12px]"
        >
          <p className="text-cream-muted">
            <span className="text-cream">'vorba</span>
            <span className="mx-2 text-cream/40">·</span>/ˈvor.ba/
            <span className="mx-2 text-cream/40">·</span>{t.defTail}
          </p>
          <p className="mt-1.5 text-honey">{t.defGloss}</p>
        </motion.div>

        {/* Statement principal — Fraunces */}
        <h1 className="display max-w-[20ch] text-[30px] leading-[1.08] md:text-[52px]">
          <RevealLines lines={headline} delay={0.5} stagger={0.09} onMount />
        </h1>

        {/* Subhead per piață (US: ERP/NetSuite/AI · CH: Insellösungen/bexio) */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.95 }}
          className="mt-6 max-w-[52ch] text-[15px] leading-relaxed text-cream-soft md:text-base"
        >
          {t.sub}
        </motion.p>

        {/* CTA + microcopy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 1.1 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3"
        >
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            {...calAttrs}
            className="group inline-flex min-h-[48px] items-center gap-2 rounded-full bg-honey px-6 py-3 font-mono text-xs uppercase tracking-[0.08em] text-ink transition-colors duration-300 ease-quint hover:bg-honey-soft"
          >
            {t.cta}
            <span aria-hidden className="transition-transform duration-300 ease-quint group-hover:translate-x-1">→</span>
          </a>
        </motion.div>
      </div>
    </section>
  )
}
